import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://softwaresolutions.com.mx";
const SITE_NAME = "Software Solutions";
const DEFAULT_TITLE = "Desarrollo de Software a la Medida en Mexico | Software Solutions";
const DEFAULT_DESCRIPTION =
  "Software Solutions desarrolla aplicaciones web, sistemas empresariales y software a la medida para empresas en Mexico. Solicita una cotizacion sin costo.";
const DEFAULT_IMAGE = `${SITE_URL}/images/software_solutions.png`;

const LANGUAGE_MAP = {
  ar: "ar",
  cn: "zh-CN",
  de: "de-DE",
  en: "en-US",
  es: "es-MX",
  fr: "fr-FR",
  hi: "hi-IN",
  it: "it-IT",
  pt: "pt-BR",
  ru: "ru-RU",
};

const upsertMetaTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("data-seo-managed", "true");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, value);
    }
  });
};

const upsertLinkTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("data-seo-managed", "true");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, value);
    }
  });
};

const upsertJsonLdScript = (schema) => {
  const selector = 'script[data-seo-schema="primary"]';
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.setAttribute("data-seo-managed", "true");
    element.setAttribute("data-seo-schema", "primary");
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(schema);
};

const Seo = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_IMAGE,
  robots = "index,follow",
  schema,
}) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const currentUrl = new URL(path, SITE_URL).toString();
    const activeLang = LANGUAGE_MAP[i18n.resolvedLanguage] || "es-MX";
    const pageSchema =
      schema ||
      [
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          logo: `${SITE_URL}/images/logo.png`,
        },
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE_NAME,
          url: SITE_URL,
        },
      ];

    document.title = title;
    document.documentElement.lang = activeLang;

    upsertMetaTag('meta[name="description"]', {
      name: "description",
      content: description,
    });
    upsertMetaTag('meta[name="robots"]', {
      name: "robots",
      content: robots,
    });
    upsertMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    upsertMetaTag('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE_NAME,
    });
    upsertMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: title,
    });
    upsertMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: currentUrl,
    });
    upsertMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: image,
    });
    upsertMetaTag('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    });
    upsertMetaTag('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    });
    upsertMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: title,
    });
    upsertMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMetaTag('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: image,
    });
    upsertLinkTag('link[rel="canonical"]', {
      rel: "canonical",
      href: currentUrl,
    });
    upsertJsonLdScript(pageSchema);
  }, [description, i18n.resolvedLanguage, image, path, robots, schema, title]);

  return null;
};

export default Seo;
